import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, FlaskConical, Upload, FileCode, Copy, CheckCircle, AlertTriangle,
  Shield, Gauge, Activity, Zap, GitBranch, BarChart3, Target, Clock, Bug,
  Download, ChevronDown, Layers,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MetricCard = ({ value, label, color, icon: Icon }) => (
  <div style={{ padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.5rem', textAlign: 'center', minWidth: 0 }}>
    {Icon && <Icon size={14} style={{ margin: '0 auto 0.25rem', opacity: 0.5 }} />}
    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: color || 'inherit', lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.125rem' }}>{label}</div>
  </div>
);

const SeverityBadge = ({ severity }) => {
  const c = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280' };
  return <span style={{ padding: '0.125rem 0.375rem', borderRadius: '999px', fontSize: '0.5625rem', fontWeight: 700, background: `${c[severity] || '#6b7280'}20`, color: c[severity] || '#6b7280', textTransform: 'uppercase' }}>{severity}</span>;
};

export default function TestingPage() {
  const { projectId } = useParams();
  const [tab, setTab] = useState('convert');
  const [projects, setProjects] = useState([]);
  const [selProject, setSelProject] = useState(projectId || '');
  const [coverage, setCoverage] = useState(null);
  const [reliability, setReliability] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [regression, setRegression] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const [outputFormat, setOutputFormat] = useState('pytest');
  const [generatedCode, setGeneratedCode] = useState('');
  const [generating, setGenerating] = useState(false);
  const [parsedCases, setParsedCases] = useState([]);
  const [uploadStatus, setUploadStatus] = useState('');
  const [copied, setCopied] = useState(false);
  const [testMapping, setTestMapping] = useState('');
  const [testCount, setTestCount] = useState(0);
  const [inputCaseCount, setInputCaseCount] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [viewMode, setViewMode] = useState('split');

  // Split generated code into individual test functions
  const splitTests = (code) => {
    if (!code) return [];
    const lines = code.split('\n');
    const tests = [];
    let current = null;
    let imports = '';
    let foundFirst = false;

    for (const line of lines) {
      // Detect test function start (pytest, cypress, junit, nunit, xunit, apex, robot)
      const pyMatch = line.match(/^\s*(def (test_\w+))/);
      const pyAsyncMatch = line.match(/^\s*(async def (test_\w+))/);
      const jsMatch = line.match(/^\s*(?:it|test)\s*\(\s*['"`](.+?)['"`]/);
      const junitMatch = line.match(/^\s*(?:@Test[\s\S]*?)?(?:public\s+)?void\s+(test\w+)/);
      const csharpMatch = line.match(/^\s*(?:\[(?:Test|Fact|Theory)\][\s\S]*?)?public\s+(?:void|async\s+Task)\s+(\w+)/);
      const apexMatch = line.match(/^\s*(?:@isTest[\s\S]*?)?(?:static\s+)?(?:void|testmethod)\s+(test\w+)/i);
      const robotMatch = line.match(/^(\*{3}\s*Test Cases\s*\*{3}|[A-Z][A-Za-z0-9 _-]+$)/);

      if (pyMatch || pyAsyncMatch || jsMatch || junitMatch || csharpMatch || apexMatch) {
        if (current) tests.push(current);
        const name = pyMatch ? pyMatch[2] : pyAsyncMatch ? pyAsyncMatch[2] : jsMatch ? jsMatch[1] : junitMatch ? junitMatch[1] : csharpMatch ? csharpMatch[1] : apexMatch ? apexMatch[1] : 'test';
        current = { name, code: line + '\n', docstring: '' };
        foundFirst = true;
      } else if (current) {
        current.code += line + '\n';
        // Extract docstring
        const docMatch = line.match(/"""(.+?)"""/);
        if (docMatch && !current.docstring) current.docstring = docMatch[1];
        const tripleStart = line.match(/^\s+"""(.+)/);
        if (tripleStart && !current.docstring) current.docstring = tripleStart[1].replace('"""', '');
      } else if (!foundFirst) {
        imports += line + '\n';
      }
    }
    if (current) tests.push(current);

    // Prepend imports to first test for context
    if (tests.length > 0 && imports.trim()) {
      tests[0].imports = imports.trim();
    }

    if (tests.length === 0) return [{ name: 'Generated Test Suite', code, docstring: '' }];
    return tests;
  };
  const [complianceCode, setComplianceCode] = useState('');
  const [loadCode, setLoadCode] = useState('');
  const [loadFw, setLoadFw] = useState('k6');
  const [genLoading, setGenLoading] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/devops/projects`, { credentials: 'include' })
      .then(r => r.json()).then(d => {
        setProjects(d.projects || []);
        if (!selProject && d.projects?.length) setSelProject(d.projects[0].id);
      }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selProject) return;
    const base = `${API_URL}/api/testing/projects/${selProject}`;
    Promise.all([
      fetch(`${base}/coverage`).then(r => r.json()).catch(() => ({})),
      fetch(`${base}/reliability`).then(r => r.json()).catch(() => ({})),
      fetch(`${base}/gap-analysis`).then(r => r.json()).catch(() => ({})),
      fetch(`${base}/regression`).then(r => r.json()).catch(() => ({})),
    ]).then(([cov, rel, gap, reg]) => {
      setCoverage(cov.coverage); setReliability(rel.reliability);
      setGaps(gap.gaps); setRegression(reg.regression);
    });
  }, [selProject]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadStatus('Parsing...');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/testing/parse-file`, { method: 'POST', body: formData, credentials: 'include' });
      const data = await res.json();
      if (data.test_cases) {
        setParsedCases(data.test_cases);
        if (data.test_cases[0]?.raw_text) setManualInput(data.test_cases[0].raw_text.slice(0, 3000));
        setUploadStatus(`Parsed ${data.count} items from ${data.filename}`);
      }
    } catch (err) { setUploadStatus('Parse failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const generateTests = async () => {
    setGenerating(true); setGeneratedCode('');
    try {
      const res = await fetch(`${API_URL}/api/testing/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ input: manualInput, parsed_cases: parsedCases, format: outputFormat, project_id: selProject }),
      });
      const data = await res.json();
      
      if (data.async && data.job_id) {
        // Poll for async job completion
        const pollJob = async () => {
          const jobRes = await fetch(`${API_URL}/api/testing/generate/${data.job_id}`, { credentials: 'include' });
          const job = await jobRes.json();
          if (job.status === 'completed' && job.result) {
            setGeneratedCode(job.result.code || '');
            setTestMapping(job.result.mapping || '');
            setTestCount(job.result.test_count || 0);
            setInputCaseCount(job.result.input_cases || 0);
            setAccuracy(job.result.accuracy || 0);
            setGenerating(false);
          } else if (job.status === 'failed') {
            setGeneratedCode(`// Generation failed: ${job.error || 'Unknown error'}`);
            setGenerating(false);
          } else {
            setUploadStatus(`Generating batch ${job.progress || 0}/${job.total_batches || '?'}...`);
            setTimeout(pollJob, 3000);
          }
        };
        setUploadStatus('Generating tests in background...');
        setTimeout(pollJob, 5000);
      } else {
        setGeneratedCode(data.code || '');
        setTestMapping(data.mapping || '');
        setTestCount(data.test_count || 0);
        setInputCaseCount(data.input_cases || 0);
        setAccuracy(data.accuracy || 0);
        setGenerating(false);
        setUploadStatus('');
      }
    } catch (err) { setGeneratedCode('// Error generating tests'); setGenerating(false); }
  };

  const generateCompliance = async () => {
    setGenLoading('compliance');
    try {
      const res = await fetch(`${API_URL}/api/testing/generate-compliance`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ project_id: selProject, types: ['gdpr', 'access_control', 'encryption', 'input_validation'] }),
      });
      const data = await res.json();
      setComplianceCode(data.code || '');
    } catch (err) { setComplianceCode('// Error'); }
    setGenLoading('');
  };

  const generateLoad = async () => {
    setGenLoading('load');
    try {
      const res = await fetch(`${API_URL}/api/testing/generate-load`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ project_id: selProject, framework: loadFw }),
      });
      const data = await res.json();
      setLoadCode(data.code || '');
    } catch (err) { setLoadCode('// Error'); }
    setGenLoading('');
  };

  const copyCode = (text) => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const downloadCode = (code, format) => {
    const extensions = { pytest: 'py', playwright: 'py', cypress: 'js', postman: 'json', junit: 'java', testng: 'java', nunit: 'cs', xunit: 'cs', apex: 'cls', robot: 'robot', selenium: 'py', k6: 'js', locust: 'py' };
    const ext = extensions[format] || 'txt';
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `generated_tests.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { key: 'convert', label: 'Manual to Auto', icon: Upload },
    { key: 'coverage', label: 'Coverage', icon: BarChart3 },
    { key: 'reliability', label: 'Reliability', icon: Activity },
    { key: 'regression', label: 'Regression', icon: GitBranch },
    { key: 'performance', label: 'Performance', icon: Zap },
    { key: 'compliance', label: 'Compliance', icon: Shield },
  ];

  return (
    <div data-testid="testing-page" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Link to="/history" style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.75rem' }}>
        <ArrowLeft size={14} /> Back
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 700, letterSpacing: '-0.02em' }}>
          <FlaskConical size={22} style={{ display: 'inline', verticalAlign: '-3px', marginRight: '0.5rem', color: 'hsl(var(--accent))' }} />
          Testing Intelligence
        </h1>
        {projects.length > 0 && (
          <select value={selProject} onChange={e => setSelProject(e.target.value)} className="input"
            style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem', maxWidth: '220px' }} data-testid="testing-project-selector">
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      {/* Top Metrics */}
      <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }} data-testid="testing-metrics">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(85px, 1fr))', gap: '0.5rem' }}>
          <MetricCard value={coverage?.total_functions || 0} label="Total Tests" icon={FlaskConical} />
          <MetricCard value={`${coverage?.line || 0}%`} label="Coverage" color={coverage?.line >= 70 ? '#10b981' : '#f59e0b'} icon={BarChart3} />
          <MetricCard value={`${reliability?.stability_score || 0}%`} label="Stability" color={reliability?.stability_score >= 85 ? '#10b981' : '#f59e0b'} icon={Activity} />
          <MetricCard value={reliability?.avg_runtime || 'N/A'} label="Avg Runtime" icon={Clock} />
          <MetricCard value={reliability?.flaky_tests || 0} label="Flaky Tests" color={reliability?.flaky_tests > 0 ? '#ef4444' : '#10b981'} icon={Bug} />
          <MetricCard value={regression?.risk_score || 0} label="Regression Risk" color={regression?.risk_score > 50 ? '#ef4444' : '#10b981'} icon={Target} />
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="tab-list" style={{ marginBottom: '1rem' }}>
        {tabs.map(t => (
          <button key={t.key} className={`tab-trigger ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}
            data-testid={`testing-tab-${t.key}`} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* Manual → Auto Conversion */}
      {tab === 'convert' && (
        <div data-testid="convert-panel">
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Manual to Automated Test Conversion</h3>
            {/* Upload */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <input type="file" ref={fileRef} onChange={handleFileUpload} accept=".csv,.xlsx,.xls,.pdf,.docx,.doc,.txt" style={{ display: 'none' }} data-testid="test-file-input" />
              <button className="btn btn-secondary" onClick={() => fileRef.current?.click()} data-testid="upload-test-file-btn" style={{ fontSize: '0.8125rem' }}>
                <Upload size={14} style={{ marginRight: '0.375rem' }} /> Upload File
              </button>
              <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))' }}>Excel, CSV, PDF, Word, or text</span>
              {uploadStatus && <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--accent))' }}>{uploadStatus}</span>}
            </div>
            {/* Input */}
            <textarea
              className="input" value={manualInput} onChange={e => setManualInput(e.target.value)}
              placeholder={"Paste manual test cases here...\n\nExample:\n1. Login with valid credentials → Should redirect to dashboard\n2. Login with invalid password → Should show error message\n3. Login with empty fields → Should show validation errors"}
              rows={8} style={{ width: '100%', fontSize: '0.8125rem', fontFamily: 'monospace', marginBottom: '0.75rem', resize: 'vertical' }}
              data-testid="manual-test-input"
            />
            {/* Output format */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Output:</span>
              {[
                { key: 'pytest', label: 'Pytest' },
                { key: 'playwright', label: 'Playwright' },
                { key: 'cypress', label: 'Cypress' },
                { key: 'selenium', label: 'Selenium' },
                { key: 'junit', label: 'JUnit (Java)' },
                { key: 'testng', label: 'TestNG (Java)' },
                { key: 'nunit', label: 'NUnit (.NET)' },
                { key: 'xunit', label: 'xUnit (.NET)' },
                { key: 'apex', label: 'Apex (Salesforce)' },
                { key: 'robot', label: 'Robot Framework' },
                { key: 'postman', label: 'Postman' },
              ].map(f => (
                <button key={f.key} className={`btn ${outputFormat === f.key ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setOutputFormat(f.key)} style={{ fontSize: '0.6875rem', padding: '0.25rem 0.625rem' }}
                  data-testid={`format-${f.key}`}>{f.label}</button>
              ))}
              <button className="btn btn-primary" onClick={generateTests} disabled={generating || !manualInput.trim()}
                style={{ marginLeft: 'auto', fontSize: '0.8125rem' }} data-testid="generate-tests-btn">
                {generating ? <><div className="spinner" style={{ width: '14px', height: '14px', marginRight: '0.375rem' }} /> Generating...</> : <><Zap size={14} style={{ marginRight: '0.375rem' }} /> Generate Tests</>}
              </button>
            </div>
          </div>
          {/* Generated Output */}
          {generatedCode && (
            <div className="card" data-testid="generated-tests-output">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h4 style={{ fontSize: '0.8125rem', fontWeight: 600 }}><FileCode size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '0.375rem' }} />Generated Tests ({outputFormat})</h4>
                <div style={{ display: 'flex', gap: '0.375rem' }}>
                  <button className="btn btn-ghost" onClick={() => downloadCode(generatedCode, outputFormat)} style={{ fontSize: '0.6875rem' }} data-testid="download-generated">
                    <Download size={12} style={{ marginRight: '0.25rem' }} /> Download
                  </button>
                  <button className="btn btn-ghost" onClick={() => copyCode(generatedCode)} style={{ fontSize: '0.6875rem' }} data-testid="copy-generated">
                    {copied ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                </div>
              </div>
              {testCount > 0 && (
                <div style={{
                  display: 'flex', gap: '1rem', marginBottom: '0.75rem', padding: '0.5rem 0.75rem',
                  background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.75rem', flexWrap: 'wrap',
                }} data-testid="test-mapping-info">
                  <span><strong>{inputCaseCount}</strong> manual cases</span>
                  <span style={{ color: 'hsl(var(--accent))' }}>&rarr;</span>
                  <span><strong>{testCount}</strong> automated tests</span>
                  <span style={{ color: accuracy >= 100 ? '#10b981' : accuracy >= 80 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                    {accuracy >= 100 ? 'Full 1:1 coverage' : `${accuracy}% mapped`}
                  </span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: accuracy >= 95 ? '#10b981' : accuracy >= 75 ? '#f59e0b' : '#ef4444' }}>
                    Accuracy: {accuracy}%
                  </span>
                </div>
              )}
              {/* View toggle */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <button className={`btn ${viewMode === 'split' ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ fontSize: '0.6875rem', padding: '0.25rem 0.625rem' }}
                  onClick={() => setViewMode('split')} data-testid="view-split-btn">
                  1:1 Split View
                </button>
                <button className={`btn ${viewMode === 'full' ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ fontSize: '0.6875rem', padding: '0.25rem 0.625rem' }}
                  onClick={() => setViewMode('full')} data-testid="view-full-btn">
                  Full Code
                </button>
              </div>
              {viewMode === 'full' ? (
                <pre style={{ background: 'hsl(var(--background))', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.6875rem', overflow: 'auto', maxHeight: '400px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {generatedCode}
                </pre>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', maxHeight: '500px', overflow: 'auto' }}>
                  {splitTests(generatedCode).map((t, idx) => (
                    <div key={idx} style={{
                      border: '1px solid hsl(var(--border))', borderRadius: '0.5rem', overflow: 'hidden',
                    }} data-testid={`split-test-${idx}`}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '0.5rem 0.75rem', background: 'hsl(var(--secondary))', borderBottom: '1px solid hsl(var(--border))',
                      }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 650, color: 'hsl(var(--accent))' }}>
                          #{idx + 1} {t.name}
                        </span>
                        <button className="btn btn-ghost" style={{ fontSize: '0.625rem', padding: '0.125rem 0.375rem' }}
                          onClick={() => copyCode(t.code)}>
                          <Copy size={10} /> Copy
                        </button>
                      </div>
                      {t.docstring && (
                        <div style={{ padding: '0.375rem 0.75rem', fontSize: '0.6875rem', color: 'hsl(var(--muted-foreground))', background: 'hsl(var(--card))', borderBottom: '1px solid hsl(var(--border))' }}>
                          {t.docstring}
                        </div>
                      )}
                      <pre style={{
                        padding: '0.5rem 0.75rem', fontSize: '0.6875rem', lineHeight: 1.6,
                        whiteSpace: 'pre-wrap', margin: 0, maxHeight: '250px', overflow: 'auto',
                        background: '#1a1a2e', color: '#e0e0e0',
                        fontFamily: "JetBrains Mono, Fira Code, Consolas, monospace",
                      }}>{t.code.trim()}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Coverage Intelligence */}
      {tab === 'coverage' && (
        <div data-testid="coverage-panel">
          {coverage ? (
            <>
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '50%',
                      border: `4px solid ${coverage.risk_score > 50 ? '#ef4444' : coverage.risk_score > 25 ? '#f59e0b' : '#10b981'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                    }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: coverage.risk_score > 50 ? '#ef4444' : coverage.risk_score > 25 ? '#f59e0b' : '#10b981' }}>{coverage.risk_score}</div>
                      <div style={{ fontSize: '0.5rem' }}>Risk</div>
                    </div>
                    <div style={{ fontSize: '0.625rem', marginTop: '0.25rem', fontWeight: 600 }}>{coverage.risk_level}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    {[
                      { label: 'Line Coverage', value: coverage.line },
                      { label: 'Branch Coverage', value: coverage.branch },
                      { label: 'Function Coverage', value: coverage.function },
                      { label: 'API Coverage', value: coverage.api },
                    ].map(item => (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                        <span style={{ fontSize: '0.75rem', width: '120px' }}>{item.label}</span>
                        <div style={{ flex: 1, height: '6px', background: 'hsl(var(--secondary))', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ width: `${item.value}%`, height: '100%', background: item.value >= 70 ? '#10b981' : item.value >= 50 ? '#f59e0b' : '#ef4444', borderRadius: '3px' }} />
                        </div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, width: '35px', textAlign: 'right' }}>{item.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {coverage.file_coverages?.length > 0 && (
                <div className="card">
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>File Coverage</h4>
                  {coverage.file_coverages.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.5rem', background: i % 2 === 0 ? 'hsl(var(--secondary))' : 'transparent', borderRadius: '0.25rem', fontSize: '0.75rem' }}>
                      <span style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.6875rem' }}>{f.path}</span>
                      <span style={{ width: '50px', textAlign: 'right' }}>{f.lines} ln</span>
                      <span style={{ width: '50px', textAlign: 'right', fontWeight: 600, color: f.coverage >= 70 ? '#10b981' : '#f59e0b' }}>{f.coverage}%</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : <div className="card"><p style={{ color: 'hsl(var(--muted-foreground))' }}>Select a project to view coverage</p></div>}
        </div>
      )}

      {/* Reliability */}
      {tab === 'reliability' && (
        <div data-testid="reliability-panel">
          {reliability ? (
            <>
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 800, color: reliability.stability_score >= 85 ? '#10b981' : reliability.stability_score >= 60 ? '#f59e0b' : '#ef4444' }}>
                      {reliability.stability_score}%
                    </div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>Test Stability Score</div>
                    <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>{reliability.stability_label}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', flex: 1, minWidth: '200px' }}>
                    <MetricCard value={reliability.flaky_tests} label="Flaky" color={reliability.flaky_tests > 0 ? '#ef4444' : '#10b981'} />
                    <MetricCard value={reliability.slow_tests} label="Slow" color={reliability.slow_tests > 0 ? '#f59e0b' : '#10b981'} />
                    <MetricCard value={reliability.nondeterministic} label="Non-Deterministic" color={reliability.nondeterministic > 0 ? '#f59e0b' : '#10b981'} />
                  </div>
                </div>
              </div>
              {reliability.recommendations?.length > 0 && (
                <div className="card">
                  <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>Recommendations</h4>
                  <ul style={{ paddingLeft: '1.25rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', lineHeight: 2 }}>
                    {reliability.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </>
          ) : <div className="card"><p style={{ color: 'hsl(var(--muted-foreground))' }}>Select a project to view reliability</p></div>}
        </div>
      )}

      {/* Regression */}
      {tab === 'regression' && (
        <div data-testid="regression-panel">
          {regression ? (
            <>
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 800, color: regression.risk_score > 60 ? '#ef4444' : regression.risk_score > 30 ? '#f59e0b' : '#10b981' }}>
                      {regression.risk_score}
                    </div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>Regression Risk</div>
                    <div style={{ fontSize: '0.625rem', color: 'hsl(var(--muted-foreground))' }}>{regression.risk_level}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '0.5rem', flex: 1 }}>
                    <MetricCard value={regression.files_changed} label="Changed" />
                    <MetricCard value={regression.files_added} label="Added" />
                    <MetricCard value={regression.files_deleted} label="Deleted" />
                    <MetricCard value={regression.impact_radius} label="Impacted" />
                    <MetricCard value={regression.new_tests_needed} label="Tests Needed" />
                  </div>
                </div>
              </div>
              {gaps && (
                <div className="card">
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Test Gap Analysis ({gaps.total} gaps found)</h4>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    {[['critical', gaps.critical], ['high', gaps.high], ['medium', gaps.medium], ['low', gaps.low]].map(([sev, count]) => (
                      <span key={sev} style={{ padding: '0.25rem 0.5rem', borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 600, background: `${sev === 'critical' ? '#ef4444' : sev === 'high' ? '#f59e0b' : sev === 'medium' ? '#3b82f6' : '#6b7280'}20`, color: sev === 'critical' ? '#ef4444' : sev === 'high' ? '#f59e0b' : sev === 'medium' ? '#3b82f6' : '#6b7280' }}>
                        {count} {sev}
                      </span>
                    ))}
                    <span style={{ marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 600 }}>Completeness: {gaps.completeness_score}%</span>
                  </div>
                  {gaps.details?.map((g, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', marginBottom: '0.375rem', fontSize: '0.75rem' }}>
                      <SeverityBadge severity={g.severity} />
                      <span style={{ fontWeight: 600, minWidth: '100px' }}>{g.category}</span>
                      <span style={{ flex: 1 }}>{g.gap}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : <div className="card"><p style={{ color: 'hsl(var(--muted-foreground))' }}>Select a project to view regression risk</p></div>}
        </div>
      )}

      {/* Performance / Load Testing */}
      {tab === 'performance' && (
        <div data-testid="performance-panel">
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Load Test Generation</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Framework:</span>
              {['k6', 'locust'].map(f => (
                <button key={f} className={`btn ${loadFw === f ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setLoadFw(f)} style={{ fontSize: '0.6875rem', padding: '0.25rem 0.625rem' }}
                  data-testid={`load-fw-${f}`}>{f}</button>
              ))}
              <button className="btn btn-primary" onClick={generateLoad} disabled={genLoading === 'load'}
                style={{ marginLeft: 'auto', fontSize: '0.8125rem' }} data-testid="generate-load-btn">
                {genLoading === 'load' ? 'Generating...' : 'Generate Load Tests'}
              </button>
            </div>
          </div>
          {loadCode && (
            <div className="card" data-testid="load-test-output">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h4 style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{loadFw} Load Test Script</h4>
                <button className="btn btn-ghost" onClick={() => copyCode(loadCode)} style={{ fontSize: '0.6875rem' }}>
                  {copied ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
              </div>
              <pre style={{ background: 'hsl(var(--background))', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.6875rem', overflow: 'auto', maxHeight: '400px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {loadCode}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Compliance Testing */}
      {tab === 'compliance' && (
        <div data-testid="compliance-testing-panel">
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Compliance Test Generation</h3>
            <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>Auto-generate GDPR, access control, encryption, and input validation tests mapped to SOC2 controls.</p>
            <button className="btn btn-primary" onClick={generateCompliance} disabled={genLoading === 'compliance'}
              style={{ fontSize: '0.8125rem' }} data-testid="generate-compliance-btn">
              {genLoading === 'compliance' ? 'Generating...' : 'Generate Compliance Tests'}
            </button>
          </div>
          {complianceCode && (
            <div className="card" data-testid="compliance-test-output">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h4 style={{ fontSize: '0.8125rem', fontWeight: 600 }}>Compliance Test Suite (pytest)</h4>
                <button className="btn btn-ghost" onClick={() => copyCode(complianceCode)} style={{ fontSize: '0.6875rem' }}>
                  {copied ? <><CheckCircle size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
              </div>
              <pre style={{ background: 'hsl(var(--background))', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.6875rem', overflow: 'auto', maxHeight: '400px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {complianceCode}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
